import type {
  AlertLevel,
  BenignAlertLevel,
  DataLoadStatus,
  LineString2d,
  Position2d,
} from './unsorted-types';
import type {
  CarComponents,
  LegacyCarComponents,
  TaskEvent,
  VicMode,
} from '@motional-cc/fe/interface/api/api-concierge';
import type {
  GEN1Vehicle,
  GEN2Vehicle,
  GenericPackage,
  Pkg,
  Vehicle as RegistrarVehicle,
} from '@motional-cc/fe/interface/api/registrar';
import {
  CommandState,
  PendingCommandState,
  RejectedCommandState,
} from '@motional-cc/fe/interface/api/api-cerebro';
import { isNullish } from 'src/tools/types';
import {
  AD_VIC_MODES,
  BENIGN_ALERT_LEVELS,
  PENDING_COMMAND_STATES,
  REJECTED_COMMAND_STATES,
  STOP_POLLING_STATES,
} from './unsorted-type-arrays';

export const GENERIC_FORMAT = 'generic_package' as const;
export const isGenericPackage = (pkg: Pkg): pkg is GenericPackage =>
  pkg.target_format === GENERIC_FORMAT;

// export const isLegacyVehicle = (
//   vehicle: RegistrarVehicle,
// ): vehicle is LegacyVehicle => vehicle.platform === 'BMW';

export const isGEN1Vehicle = (
  vehicle?: Pick<RegistrarVehicle, 'platform'> | null,
): vehicle is GEN1Vehicle => !!vehicle?.platform?.startsWith('GEN1');

export const isGEN2Vehicle = (
  vehicle?: Pick<RegistrarVehicle, 'platform'> | null,
): vehicle is GEN2Vehicle => !!vehicle?.platform?.startsWith('GEN2');

// export const isGEN3Vehicle = (
//   vehicle: RegistrarVehicle,
// ): vehicle is GEN3Vehicle => vehicle.platform?.startsWith('GEN3');

export const isIncompleteStatus = (
  status: DataLoadStatus,
): status is 'pending' => ['pending'].includes(status);

export const isModernCarComponents = (
  carComponents: CarComponents | LegacyCarComponents,
): carComponents is CarComponents =>
  isNullish(
    (carComponents as LegacyCarComponents).nuCarClient ||
      (carComponents as LegacyCarComponents).nuCarStateManager ||
      (carComponents as LegacyCarComponents).nuCarCamVideoStreamer,
  );

export const isBenignAlertLevel = (
  alertLevel: AlertLevel,
): alertLevel is BenignAlertLevel =>
  BENIGN_ALERT_LEVELS.includes(alertLevel as BenignAlertLevel);

export const isSameTaskEvent = (task1?: TaskEvent, task2?: TaskEvent) =>
  !!(task1 && task2) &&
  task1.name === task2.name &&
  task1.goalId === task2.goalId;

export const isRejectedCommandState = (
  state?: CommandState,
): state is RejectedCommandState =>
  !!state && REJECTED_COMMAND_STATES.includes(state as RejectedCommandState);

export const isPendingCommandState = (
  state?: CommandState,
): state is PendingCommandState =>
  !!state && PENDING_COMMAND_STATES.includes(state as PendingCommandState);

export const isShouldStopPollingState = (state?: CommandState): boolean =>
  !!state && STOP_POLLING_STATES.includes(state);

export const isHTMLElement = (value?: unknown): value is HTMLElement => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  if (!('nodeType' in value)) {
    return false;
  }

  return true;
};

const isPosition2d = (value?: unknown): value is Position2d =>
  Array.isArray(value) && value.length >= 2;

export const isLineString2d = (value?: unknown): value is LineString2d =>
  Array.isArray(value) && value.every((position) => isPosition2d(position));

export const isAdMode = (vicModeManager?: VicMode): boolean =>
  AD_VIC_MODES.includes(vicModeManager ?? 'Unknown');
